import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { Checkbox } from "../../Checkbox";
import { PartnerOnboardingService } from "../../../api/generated/services/PartnerOnboardingService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface ApproveApplicationModalProps {
  isOpen: boolean;
  applicationId: string;
  companyName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ApproveApplicationModal: React.FC<ApproveApplicationModalProps> = ({
  isOpen,
  applicationId,
  companyName,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [automaticallyInviteAdmin, setAutomaticallyInviteAdmin] =
    useState(false);
  const [automaticallyConfigureBilling, setAutomaticallyConfigureBilling] =
    useState(true);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setAutomaticallyInviteAdmin(false);
      setAutomaticallyConfigureBilling(false);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!applicationId) {
      setError("Application ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await PartnerOnboardingService.postApiPartnerOnboardingApproveApplication(
        {
          partnerApplicationId: applicationId,
          automaticallyInviteCompanyAdmin: automaticallyInviteAdmin,
          automaticallyConfigureBilling: automaticallyConfigureBilling,
        }
      );
      // Reset state and close modal directly (bypass handleClose's loading check)
      setError(null);
      setAutomaticallyInviteAdmin(false);
      setAutomaticallyConfigureBilling(false);
      setLoading(false);
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error approving application:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to approve application. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Approve Application"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Approve Application</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            You are about to approve the partner application for{" "}
            <span className="font-medium">{companyName}</span>.
          </p>
          <p className="text-sm text-blue-800 mt-2">
            This will initiate the onboarding process for the company.
          </p>
        </div>

        <div className="py-2">
          <Checkbox
            checked={automaticallyInviteAdmin}
            onChange={(e) => setAutomaticallyInviteAdmin(e.target.checked)}
            label="Automatically invite company admin"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            If checked, an invitation email will be sent to the contact email
            address to set up their admin account.
          </p>
        </div>

        <div className="py-2">
          <Checkbox
            checked={automaticallyConfigureBilling}
            onChange={(e) => setAutomaticallyConfigureBilling(e.target.checked)}
            label="Automatically configure billing"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            If checked, billing will be configured for the company in Billwerk
            automatically.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={loading}>
            Approve
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ApproveApplicationModal;
